/**
 * @fileoverview The "Plant Doctor" section of a public plant's page
 * (/@amy/cherokee-purple): the checkups the gardener saved to the plant's
 * notes and did not hide, newest first, three at first and then "Show all".
 *
 * Each card is words only (the page's photos are at its top, once): the
 * likely problem's name, "What to do" (the problem's first step from the
 * library, written for anyone), and a
 * foot line "Saved by @amy · Oct 10" with "Sep 28 photo ↑", which turns the
 * page's photo scroller to the photo the checkup looked at and scrolls up to
 * it. No seriousness chip: the checkup's own "serious" call is not sure
 * enough to show strangers (it called a squash vine borer not serious). An
 * "About <problem>" link shows when the problem has a library page of
 * its own (none does yet, so none is drawn).
 *
 * What Grow saw is never here: it is written to the gardener ("your photo")
 * and stays in the app. Nor are the gardener's own words about the plant or
 * the full answer; the backend sends only these parts
 * (services/gardens/publicGarden).
 *
 * The "1 checkup" link under a plant's name on the garden page opens the
 * plant's page at this section (#checkups); the section scrolls itself into
 * view when the address says so.
 *
 * Where: GardenItemPage (a plant's page), under its name.
 * API: none (the page's data is gardens getGardenItem).
 * Settings: none (three show at first; the section's id is gardenPageSections' CHECKUPS_SECTION_ID).
 * Words (en/es): gardenPage.page.checkupsHeading, checkupDo, checkupAbout,
 * checkupSaved, checkupPhoto, checkupsShowAll,
 * checkupsShowFewer.
 *
 * @module CheckupsSection
 */

import React from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowRight, ArrowUp } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { PublicCheckup } from '@/shared/api/gardens';
import { CHECKUPS_SECTION_ID, SECTION_HEADING_CLASS } from './gardenPageSections';
import { useShortDay } from './lib/gardenPage';

/** How many checkups show before "Show all". */
const CHECKUPS_SHOWN_FIRST = 3;

/** What the section is told: the plant's checkups, newest first. */
interface CheckupsSectionProps {
  /** The checkups the page may show; the section draws nothing for none. */
  checkups: PublicCheckup[];
  /** Who saved them, as the page names the gardener ("@amy"), or null. */
  by: string | null;
  /** Turns the page's photo scroller to the photo of this day and scrolls up to it; none hides the link. */
  onShowPhoto?: (date: string) => void;
}

/**
 * One checkup as a card.
 *
 * @param props.checkup - The checkup.
 * @param props.by - Who saved it, or null.
 * @param props.onShowPhoto - Shows the checkup's photo at the page's top, if given.
 */
const CheckupCard: React.FC<{ checkup: PublicCheckup; by: string | null; onShowPhoto?: (date: string) => void }> = ({ checkup, by, onShowPhoto }) => {
  const shortDay = useShortDay();
  const { reactTranslate: t, i18n } = useTranslation();
  /** The language the library words are shown in. */
  const lang = i18n.language.startsWith('es') ? 'es' : 'en';
  const problem = checkup.problem[lang] || checkup.problem.en;
  const step = checkup.step ? checkup.step[lang] || checkup.step.en : null;
  const day = shortDay(checkup.date) ?? checkup.date;
  const photoDay = checkup.photo?.date ?? null;
  return (
    <li className="rounded-2xl border bg-card p-4">
      <h3 className="font-grow text-lg font-bold leading-snug [overflow-wrap:anywhere]">{problem}</h3>
      {step && (
        <div className="mt-3">
          <p className="text-sm font-bold">{t('gardenPage.page.checkupDo')}</p>
          <p className="mt-0.5 text-sm leading-relaxed [overflow-wrap:anywhere]">{step}</p>
        </div>
      )}
      {checkup.aboutUrl && (
        <a
          href={checkup.aboutUrl}
          className="inline-flex min-h-[44px] items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          {t('gardenPage.page.checkupAbout', { problem })}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </a>
      )}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 border-t pt-2 text-sm text-muted-foreground">
        <span className="min-w-0 [overflow-wrap:anywhere]">{by ? t('gardenPage.page.checkupSaved', { name: by, day }) : day}</span>
        {photoDay && onShowPhoto && (
          <button
            type="button"
            onClick={() => onShowPhoto(photoDay)}
            className="inline-flex min-h-[44px] items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
          >
            {t('gardenPage.page.checkupPhoto', { day: shortDay(photoDay) ?? photoDay })}
            <ArrowUp className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </li>
  );
};

/**
 * The Plant Doctor section: three cards, then "Show all" for the rest.
 * Scrolls itself into view when the page's address ends in #checkups.
 *
 * @param props - CheckupsSectionProps.
 */
export const CheckupsSection: React.FC<CheckupsSectionProps> = ({ checkups, by, onShowPhoto }) => {
  const { reactTranslate: t } = useTranslation();
  const { hash } = useLocation();
  const [all, setAll] = React.useState(false);
  const shown = all ? checkups : checkups.slice(0, CHECKUPS_SHOWN_FIRST);
  const wanted = hash === `#${CHECKUPS_SECTION_ID}` && checkups.length > 0;

  React.useEffect(() => {
    if (!wanted) return;
    const el = document.getElementById(CHECKUPS_SECTION_ID);
    if (!el) return;
    const calm = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: calm ? 'auto' : 'smooth', block: 'start' });
  }, [wanted]);

  if (checkups.length === 0) return null;
  return (
    <section id={CHECKUPS_SECTION_ID} tabIndex={-1} className="scroll-mt-4 focus:outline-none">
      <h2 className={SECTION_HEADING_CLASS}>{t('gardenPage.page.checkupsHeading')}</h2>
      <ul className="mt-3 space-y-3">
        {shown.map((checkup) => <CheckupCard key={checkup.id} checkup={checkup} by={by} onShowPhoto={onShowPhoto} />)}
      </ul>
      {checkups.length > CHECKUPS_SHOWN_FIRST && (
        <button
          type="button"
          onClick={() => setAll(!all)}
          className="mt-3 min-h-[44px] text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          {all ? t('gardenPage.page.checkupsShowFewer') : t('gardenPage.page.checkupsShowAll', { count: checkups.length })}
        </button>
      )}
    </section>
  );
};
